import React from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { connect } from "react-redux";
import { Form, Input, Button, Typography } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import FormItemCheckbox from "./FormItemCheckbox";
import LoaSelector from "../loa/LoaSelector";

const { Paragraph } = Typography;

const required = (value) => (value ? undefined : "Required");
const minLength = (min) => (value) =>
  value && value.length < min ? `Must be ${min} characters or more` : undefined;
const minLength3 = minLength(3);
const maxLength = (max) => (value) =>
  value && value.length > max ? `Must be ${max} characters or less` : undefined;
const maxLength64 = maxLength(64);
const nospace = (value) =>
  value && !/^\S+$/.test(value) ? "Cannot contain a space" : undefined;
const identifier = (value) =>
  value && !/^[a-z][a-z0-9\-_]+$/.test(value)
    ? "Must start with a lowercase letter, can contain only lowercase letters, numbers, dashes and underscores"
    : undefined;

const NewRoleForm = ({ submitFunction, initialValues, isEdit }) => {
  const defaultValues = {
    displayName: initialValues?.displayName || "",
    name: initialValues?.name || "",
    description: initialValues?.description || "",
    required: initialValues?.required || false,
    multifactor: initialValues?.multifactor || false,
    applyToAllUsers: initialValues?.applyToAllUsers || false,
    minimumLoaId: initialValues?.minimumLoaId || null,
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues,
  });

  const onSubmit = (data) => {
    // If editing we have to add back in the base fields that are not part of the form
    if (initialValues) {
      data.name = initialValues.name;
      data.id = initialValues.id;
      data.applicationId = initialValues.applicationId;
    }
    submitFunction(data);
    return;
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <div style={{ marginBottom: 16 }} />
      {!isEdit && (
        <>
          <Form.Item
            style={{ marginBottom: 8 }}
            label={
              <span
                style={{
                  display: "block",
                  fontWeight: "bold",
                }}
              >
                Role Identifier
              </span>
            }
          >
            <Controller
              name="name"
              control={control}
              rules={{
                validate: {
                  minLength3,
                  nospace,
                  identifier,
                  maxLength64,
                  required,
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="nameField"
                  placeholder="This will be the role's identifier in tokens"
                />
              )}
            />

            {errors.name && (
              <Paragraph style={{ color: "red", marginTop: 4 }}>
                {errors.name.message}
              </Paragraph>
            )}
          </Form.Item>
        </>
      )}

      <Form.Item
        style={{ marginBottom: 8 }}
        label={
          <span
            style={{
              display: "block",
              fontWeight: "bold",
            }}
          >
            Role Name
          </span>
        }
      >
        <Controller
          name="displayName"
          control={control}
          rules={{ validate: { required } }}
          render={({ field }) => (
            <Input
              {...field}
              id="displayNameField"
              placeholder="This will be the role's display name in the UI"
            />
          )}
        />
        {errors.displayName && (
          <Paragraph style={{ color: "red", marginTop: 4 }}>
            {errors.displayName.message}
          </Paragraph>
        )}
      </Form.Item>

      <Form.Item
        style={{ marginBottom: 16 }}
        label={
          <span
            style={{
              display: "block",
              fontWeight: "bold",
            }}
          >
            Description
          </span>
        }
      >
        <Controller
          name="description"
          control={control}
          rules={{ validate: { required } }}
          render={({ field }) => (
            <Input
              {...field}
              id="descriptionField"
              placeholder="Brief description, e.g. what is this role used for?"
            />
          )}
        />
        {errors.description && (
          <Paragraph style={{ color: "red", marginTop: 4 }}>
            {errors.description.message}
          </Paragraph>
        )}
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <FormItemCheckbox
          name="required"
          label={<>This role is required to access my application</>}
          paragraph="If checked, users without this role will not be able to authenticate to your application
          "
          control={control}
          required={false}
          error={null}
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <FormItemCheckbox
          name="multifactor"
          label={<>Multifactor authentication required</>}
          paragraph="If checked, users must authenticate with Multifactor Authentication to be granted this role."
          control={control}
          required={false}
          error={null}
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <FormItemCheckbox
          name="applyToAllUsers"
          label={<>This role applies to all authenticated users</>}
          paragraph={`
            If checked, this role will applied to all authenticated users,
            regardless of them belonging to any group or not. Use this
            option to define a role that is based only on the value of the
            Minimum Level of Assurance and/or usage of Multifactor
            authentication.`}
          control={control}
          required={false}
          error={null}
        />
      </Form.Item>

      <Form.Item
        style={{ marginBottom: 16 }}
        label={
          <span
            style={{
              marginTop: 3,
              display: "block",
              fontWeight: "bold",
            }}
          >
            Minimum Level of Assurance
          </span>
        }
      >
        <Controller
          name="minimumLoaId"
          control={control}
          render={({ field }) => (
            <LoaSelector parentInitialValue={initialValues} input={field} />
          )}
        />
      </Form.Item>

      <Form.Item
        style={{
          textAlign: "right",
          marginBottom: 0,
        }}
      >
        <Button
          type="primary"
          htmlType="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={Object.keys(errors).length > 0}
          id="newRoleSubmitButton"
          icon={<CheckOutlined />}
        >
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
};

NewRoleForm.propTypes = {
  submitFunction: PropTypes.func,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default connect(() => ({}))(NewRoleForm);
